import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  CONSENT_TYPES,
  CURRENT_CONSENT_DOCUMENT_VERSION,
  type ConsentType,
} from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { SelectField } from '../../../components/forms/SelectField';
import { PageSpinner } from '../../../components/PageSpinner';
import { customersApi } from '../../../features/customers/customers.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../lib/error-message';
import styles from '../AppPages.module.css';

const CONSENT_LABELS: Record<string, string> = {
  PLATFORM_TERMS: 'Platform terms of use',
  PRIVACY_NOTICE: 'Privacy notice',
  REPAYMENT_AGREEMENT: 'Repayment agreement',
  DIRECT_DEBIT_PREPARATION: 'Direct debit preparation',
  ELECTRONIC_COMMUNICATION: 'Electronic communication',
};

export function ConsentsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const consents = useQuery({
    queryKey: ['customer', 'consents'],
    queryFn: () => customersApi.listConsents(),
  });

  const [consentType, setConsentType] = useState<ConsentType>(CONSENT_TYPES[0]);

  const record = useMutation({
    mutationFn: () => customersApi.recordConsent({ consentType }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customer', 'consents'] });
    },
  });

  const recordedTypes = new Set(consents.data?.map((item) => item.consentType) ?? []);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Consents</h1>
        <p className={styles.pageSubtitle}>
          Record acceptance of platform documents. Each consent is stored with a version for audit.
        </p>
      </header>

      <section className={styles.card} aria-labelledby="record-consent-heading">
        <h2 className={styles.cardTitle} id="record-consent-heading">
          Record consent
        </h2>

        {record.isError && <Alert tone="error">{toDisplayMessage(record.error)}</Alert>}
        {record.isSuccess && <Alert tone="success">Consent recorded.</Alert>}

        <form
          className={styles.filters}
          onSubmit={(event) => {
            event.preventDefault();
            record.mutate();
          }}
        >
          <SelectField
            label="Document"
            value={consentType}
            onChange={(event) => {
              setConsentType(event.target.value as ConsentType);
            }}
          >
            {CONSENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {CONSENT_LABELS[type] ?? type}
              </option>
            ))}
          </SelectField>
          <p className={styles.pageSubtitle}>
            Current platform document version: {CURRENT_CONSENT_DOCUMENT_VERSION}
          </p>
          <Button type="submit" disabled={record.isPending || recordedTypes.has(consentType)}>
            Accept
          </Button>
        </form>
      </section>

      <section className={styles.card} aria-labelledby="consents-heading">
        <h2 className={styles.cardTitle} id="consents-heading">
          Recorded consents
        </h2>

        {consents.isPending && <PageSpinner label="Loading consents" />}
        {consents.isError && <Alert tone="error">{toDisplayMessage(consents.error)}</Alert>}

        {consents.isSuccess && consents.data.length === 0 && (
          <p className={styles.empty}>No consents recorded yet.</p>
        )}

        {consents.isSuccess && consents.data.length > 0 && (
          <ul>
            {consents.data.map((consent) => (
              <li key={consent.id}>
                {CONSENT_LABELS[consent.consentType] ?? consent.consentType} (v
                {consent.documentVersion}) — {new Date(consent.acceptedAt).toLocaleString()}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
